// src/pages/PosteControle.js - Poste de Contrôle : centre d'opérations de la
// société. Carte interactive de tous les sites, colorée selon la gravité des
// incidents non résolus (vert = RAS, orange = mineur, rouge = majeur),
// alarme sonore à la découverte d'un nouvel incident, prise en charge
// directe, détails du site (agents en poste + contacts), remplacement d'un
// agent absent/malade, envoi d'une patrouille, délégation à la logistique
// pour les incidents matériels. Construite sur la même base que SitesMap.js
// (react-leaflet), avec un polling ~15s (pas d'infra temps réel dans le
// projet).
import React, { useState, useEffect, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Icon } from "leaflet";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";
const POLL_INTERVAL_MS = 15000;
const NIAMEY_CENTER = [13.5137, 2.1098];
const DEFAULT_ZOOM = 12;

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  success: '#4caf50',
  warning: '#ff9800',
  danger: '#f44336',
  purple: '#6D28D9',
};

const NIVEAU_LABELS = { 1: 'Majeur', 2: 'Élevé', 3: 'Normal', 4: 'Basse' };

// Gravité d'un site = pire incident non résolu qui lui est rattaché.
// niveau 1-2 -> rouge, 3-4 -> orange, aucun -> vert.
const graviteSite = (incidentsDuSite) => {
  if (!incidentsDuSite.length) return 'vert';
  const pire = Math.min(...incidentsDuSite.map(i => i.niveau?.id ?? 3));
  return pire <= 2 ? 'rouge' : 'orange';
};

const COULEUR_PAR_GRAVITE = { vert: '#4caf50', orange: '#ff9800', rouge: '#f44336' };

// Icône de marker colorée par gravité — cercle SVG (pas de dépendance à des
// images externes à deux couleurs seulement, comme SitesMap.js).
const createGraviteIcon = (gravite) => {
  const couleur = COULEUR_PAR_GRAVITE[gravite] || COULEUR_PAR_GRAVITE.vert;
  const pulse = gravite === 'rouge';
  return new Icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46">
        ${pulse ? `<circle cx="18" cy="18" r="16" fill="${couleur}" opacity="0.25"/>` : ''}
        <path d="M18 45C18 45 33 27.5 33 17C33 8.16 26.28 1 18 1C9.72 1 3 8.16 3 17C3 27.5 18 45 18 45Z"
              fill="${couleur}" stroke="white" stroke-width="2"/>
        <circle cx="18" cy="17" r="7" fill="white"/>
      </svg>
    `)}`,
    iconSize: [36, 46],
    iconAnchor: [18, 46],
    popupAnchor: [0, -40],
  });
};

// Alarme sonore synthétisée (Web Audio API) — pas de fichier audio à héberger.
const jouerAlarme = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const dureeParBip = 0.18;
    [0, 1, 2].forEach((i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = i % 2 === 0 ? 880 : 660;
      gain.gain.setValueAtTime(0.15, ctx.currentTime + i * dureeParBip);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (i + 1) * dureeParBip);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * dureeParBip);
      osc.stop(ctx.currentTime + (i + 1) * dureeParBip);
    });
    setTimeout(() => ctx.close(), 700);
  } catch (e) {
    // Audio non disponible (permissions navigateur, etc.) — silencieux.
  }
};

const PosteControle = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [sites, setSites] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mapCenter] = useState(NIAMEY_CENTER);
  const [selectedSite, setSelectedSite] = useState(null);
  const mapRef = useRef(null);
  const [siteDetail, setSiteDetail] = useState(null);
  const [loadingSiteDetail, setLoadingSiteDetail] = useState(false);
  const [remplacementAgent, setRemplacementAgent] = useState(null); // { id_agent, nom }
  const [patrouilleSite, setPatrouilleSite] = useState(null); // site pour lequel on choisit un agent à envoyer
  const [actionEnCours, setActionEnCours] = useState(null);
  const [toast, setToast] = useState(null);
  const [resolveModalIncident, setResolveModalIncident] = useState(null); // incident pour lequel on demande la description des actions de résolution
  const [actionResolutionText, setActionResolutionText] = useState("");
  const [resolveError, setResolveError] = useState("");
  const [resolveSubmitting, setResolveSubmitting] = useState(false);

  const seenIncidentIds = useRef(new Set());
  const firstLoad = useRef(true);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) {
      try {
        const u = JSON.parse(stored);
        setUserInfo({ ...u, profile: u.profile || u.poste || 0 });
      } catch {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const showToast = (message, type = "info") => {
    const id = Date.now();
    setToast({ id, message, type });
    setTimeout(() => setToast(prev => (prev?.id === id ? null : prev)), 6000);
  };

  const fetchDonnees = useCallback(async (silencieux = false) => {
    if (!silencieux) setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };

      const [sitesRes, incidentsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/sites/list.php?limit=1000&supprimer=0`, { headers }),
        axios.get(`${API_BASE_URL}/incidents/liste_incidents.php?statut=${encodeURIComponent('non résolu')}`, { headers }),
      ]);

      if (sitesRes.data?.success) {
        setSites((sitesRes.data.data || []).filter(s =>
          s.latitude_site && s.longitude_site && s.latitude_site !== 0 && s.longitude_site !== 0
        ));
      }

      if (incidentsRes.data?.success) {
        const liste = incidentsRes.data.data || [];
        setIncidents(liste);

        const idsActuels = new Set(liste.map(i => i.id_incident));
        if (!firstLoad.current) {
          const nouveaux = liste.filter(i => !seenIncidentIds.current.has(i.id_incident));
          if (nouveaux.length > 0) {
            jouerAlarme();
            showToast(
              nouveaux.length === 1
                ? `Nouvel incident sur ${nouveaux[0].site?.nom || 'un site'} : ${nouveaux[0].description_incident?.slice(0, 80) || ''}`
                : `${nouveaux.length} nouveaux incidents déclarés`,
              "danger"
            );
          }
        }
        seenIncidentIds.current = idsActuels;
        firstLoad.current = false;
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setError(err.response?.data?.message || "Impossible de charger les données du Poste de Contrôle.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!userInfo) return;
    fetchDonnees();
    const interval = setInterval(() => fetchDonnees(true), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userInfo, fetchDonnees]);

  // La colonne de la carte passe de col-lg-12 à col-lg-8 quand un site est
  // sélectionné (panneau de détail à droite) : Leaflet calcule la taille de
  // ses tuiles à l'initialisation et ne détecte pas ce changement de largeur
  // tout seul, ce qui faisait déborder/rétrécir l'affichage de la carte tant
  // qu'aucun redimensionnement de fenêtre ne déclenchait un recalcul.
  useEffect(() => {
    if (!mapRef.current) return;
    const timeout = setTimeout(() => mapRef.current.invalidateSize(), 300);
    return () => clearTimeout(timeout);
  }, [selectedSite]);

  // Incidents groupés par site (via incident.site.id, désormais fiable).
  const incidentsParSite = incidents.reduce((acc, inc) => {
    const idSite = inc.site?.id;
    if (idSite) {
      acc[idSite] = acc[idSite] || [];
      acc[idSite].push(inc);
    }
    return acc;
  }, {});

  const chargerDetailSite = async (site) => {
    setSelectedSite(site);
    setSiteDetail(null);
    setLoadingSiteDetail(true);
    try {
      const token = localStorage.getItem("access_token");
      const [detailRes, agentsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/sites/get.php?id=${site.id_site}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/affectations/agents-by-site.php?id_site=${site.id_site}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSiteDetail({
        site: detailRes.data?.site || site,
        agentsAffectes: agentsRes.data?.agents_affectes || [],
        agentsDisponibles: agentsRes.data?.agents_disponibles || [],
      });
    } catch (err) {
      showToast("Impossible de charger le détail du site.", "danger");
    } finally {
      setLoadingSiteDetail(false);
    }
  };

  const rafraichirSiteDetail = () => {
    if (selectedSite) chargerDetailSite(selectedSite);
  };

  const appelerAction = async (cle, requete) => {
    setActionEnCours(cle);
    try {
      await requete();
    } catch (err) {
      showToast(err.response?.data?.message || "Une erreur est survenue.", "danger");
    } finally {
      setActionEnCours(null);
    }
  };

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    'Content-Type': 'application/json',
  });

  const prendreEnCharge = (idIncident) => appelerAction(`charge-${idIncident}`, async () => {
    const res = await axios.post(`${API_BASE_URL}/incidents/prendre_en_charge.php`, { id_incident: idIncident }, { headers: authHeaders() });
    showToast(res.data?.message || "Incident pris en charge.", "success");
    fetchDonnees(true);
  });

  // La résolution exige de décrire les actions effectuées (champ obligatoire) :
  // ouvre un modal plutôt que de résoudre directement au clic sur le bouton.
  const ouvrirModalResolution = (incident) => {
    setResolveModalIncident(incident);
    setActionResolutionText("");
    setResolveError("");
  };

  const confirmerResolution = async () => {
    const texte = actionResolutionText.trim();
    if (!texte) {
      setResolveError("Merci de décrire les actions effectuées pour résoudre cet incident.");
      return;
    }
    setResolveError("");
    setResolveSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/incidents/resolus_incidents.php`, {
        id_incident: resolveModalIncident.id_incident,
        est_resolu: true,
        action_resolution: texte,
      }, { headers: authHeaders() });
      showToast(res.data?.message || "Incident résolu.", "success");
      setResolveModalIncident(null);
      setActionResolutionText("");
      fetchDonnees(true);
    } catch (err) {
      setResolveError(err.response?.data?.message || "Erreur lors de la résolution de l'incident.");
    } finally {
      setResolveSubmitting(false);
    }
  };

  const affecterLogistique = (idIncident) => appelerAction(`logistique-${idIncident}`, async () => {
    const res = await axios.post(`${API_BASE_URL}/incidents/affecter_logistique.php`, { id_incident: idIncident }, { headers: authHeaders() });
    showToast(res.data?.message || "Affecté à la logistique.", "success");
    fetchDonnees(true);
  });

  const marquerIndisponible = (agent, disponibilite) => appelerAction(`dispo-${agent.id_agent}`, async () => {
    await axios.post(`${API_BASE_URL}/agents/changer_disponibilite.php`,
      { id_agent: agent.id_agent, disponibilite },
      { headers: authHeaders() });
    showToast(`${agent.nom_agent} marqué "${disponibilite}".`, "success");
    setRemplacementAgent({ id_agent: agent.id_agent, nom: `${agent.prenom_agent} ${agent.nom_agent}` });
    rafraichirSiteDetail();
  });

  const remplacerAgent = (ancienAgentId, nouvelAgent) => appelerAction(`remplace-${nouvelAgent.id_agent}`, async () => {
    await axios.delete(`${API_BASE_URL}/affectations/delete.php`, {
      headers: authHeaders(),
      data: { id_site: selectedSite.id_site, id_agent: ancienAgentId },
    });
    await axios.post(`${API_BASE_URL}/affectations/create.php`,
      { id_site: selectedSite.id_site, id_agent: nouvelAgent.id_agent },
      { headers: authHeaders() });
    showToast(`${nouvelAgent.nom_agent} affecté en remplacement.`, "success");
    setRemplacementAgent(null);
    rafraichirSiteDetail();
  });

  const envoyerPatrouille = (site, agent) => appelerAction(`patrouille-${agent.id_agent}`, async () => {
    if (!agent.id_user) {
      showToast("Cet agent n'a pas de compte utilisateur — impossible de le notifier.", "danger");
      return;
    }
    const res = await axios.post(`${API_BASE_URL}/poste_controle/envoyer_patrouille.php`,
      { id_site: site.id_site, id_user_agent: agent.id_user },
      { headers: authHeaders() });
    showToast(res.data?.message || "Patrouille envoyée.", "success");
    setPatrouilleSite(null);
  });

  const canAccess = userInfo && [3, 5].includes(Number(userInfo.profile));

  if (userInfo && !canAccess) {
    return (
      <Layout>
        <div className="pagetitle"><h1>Poste de Contrôle</h1></div>
        <div className="alert alert-warning m-4">Réservé aux gestionnaires et postes de contrôle.</div>
      </Layout>
    );
  }

  const totalIncidents = incidents.length;
  const totalRouge = Object.values(incidentsParSite).filter(l => graviteSite(l) === 'rouge').length;
  const totalOrange = Object.values(incidentsParSite).filter(l => graviteSite(l) === 'orange').length;

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: 700 }}>
          <i className="bi bi-broadcast me-2"></i>
          Poste de Contrôle
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.secondary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.primary }}>Poste de Contrôle</li>
          </ol>
        </nav>
      </div>

      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, minWidth: 320, maxWidth: 420 }}>
          <div className={`alert alert-${toast.type} alert-dismissible fade show`} style={{ borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            {toast.message}
            <button type="button" className="btn-close" onClick={() => setToast(null)}></button>
          </div>
        </div>
      )}

      <section className="section">
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="row mb-3 g-3">
          <div className="col-md-3">
            <div className="card h-100" style={{ borderRadius: 12, border: `1px solid ${COLORS.lightBg}` }}>
              <div className="card-body d-flex align-items-center">
                <i className="bi bi-geo-alt me-3" style={{ fontSize: '1.6rem', color: COLORS.primary }}></i>
                <div>
                  <div style={{ fontSize: '0.8rem', color: COLORS.darkText }}>Sites suivis</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: COLORS.primary }}>{sites.length}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card h-100" style={{ borderRadius: 12, border: `1px solid ${COLORS.success}40` }}>
              <div className="card-body d-flex align-items-center">
                <i className="bi bi-shield-check me-3" style={{ fontSize: '1.6rem', color: COLORS.success }}></i>
                <div>
                  <div style={{ fontSize: '0.8rem', color: COLORS.darkText }}>Incidents actifs</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: COLORS.primary }}>{totalIncidents}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card h-100" style={{ borderRadius: 12, border: `1px solid ${COLORS.warning}40` }}>
              <div className="card-body d-flex align-items-center">
                <i className="bi bi-exclamation-triangle me-3" style={{ fontSize: '1.6rem', color: COLORS.warning }}></i>
                <div>
                  <div style={{ fontSize: '0.8rem', color: COLORS.darkText }}>Sites en orange</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: COLORS.warning }}>{totalOrange}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card h-100" style={{ borderRadius: 12, border: `1px solid ${COLORS.danger}40` }}>
              <div className="card-body d-flex align-items-center">
                <i className="bi bi-exclamation-octagon me-3" style={{ fontSize: '1.6rem', color: COLORS.danger }}></i>
                <div>
                  <div style={{ fontSize: '0.8rem', color: COLORS.darkText }}>Sites en rouge</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: COLORS.danger }}>{totalRouge}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3">
          {/* Carte */}
          <div className={selectedSite ? "col-lg-8" : "col-lg-12"}>
            <div className="card" style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${COLORS.lightBg}` }}>
              <div style={{ height: '640px', position: 'relative' }}>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="spinner-border" style={{ color: COLORS.primary }}></div>
                  </div>
                ) : (
                  <MapContainer
                    center={mapCenter}
                    zoom={DEFAULT_ZOOM}
                    style={{ height: '100%', width: '100%' }}
                    attributionControl={false}
                    whenCreated={(map) => {
                      mapRef.current = map;
                      setTimeout(() => map.invalidateSize(), 100);
                    }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {sites.map(site => {
                      const incs = incidentsParSite[site.id_site] || [];
                      const gravite = graviteSite(incs);
                      return (
                        <Marker
                          key={site.id_site}
                          position={[parseFloat(site.latitude_site), parseFloat(site.longitude_site)]}
                          icon={createGraviteIcon(gravite)}
                          eventHandlers={{ click: () => chargerDetailSite(site) }}
                        >
                          <Popup>
                            <div style={{ minWidth: 200 }}>
                              <strong>{site.nom_site}</strong><br />
                              <small>{site.nom_client}</small><br />
                              {incs.length > 0 ? (
                                <span className="badge mt-2" style={{ backgroundColor: COULEUR_PAR_GRAVITE[gravite], color: 'white' }}>
                                  {incs.length} incident{incs.length > 1 ? 's' : ''} actif{incs.length > 1 ? 's' : ''}
                                </span>
                              ) : (
                                <span className="badge mt-2" style={{ backgroundColor: COLORS.success, color: 'white' }}>RAS</span>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MapContainer>
                )}
              </div>
              <div className="p-3 border-top d-flex flex-wrap gap-4" style={{ backgroundColor: COLORS.lightBg }}>
                <div className="d-flex align-items-center"><span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: COULEUR_PAR_GRAVITE.vert, marginRight: 8 }}></span><small>RAS</small></div>
                <div className="d-flex align-items-center"><span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: COULEUR_PAR_GRAVITE.orange, marginRight: 8 }}></span><small>Incident mineur (Normal/Basse)</small></div>
                <div className="d-flex align-items-center"><span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: COULEUR_PAR_GRAVITE.rouge, marginRight: 8 }}></span><small>Incident majeur (Majeur/Élevé)</small></div>
              </div>
            </div>
          </div>

          {/* Panneau détail du site sélectionné */}
          {selectedSite && (
            <div className="col-lg-4">
              <div className="card" style={{ borderRadius: 12, border: `1px solid ${COLORS.lightBg}` }}>
                <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: COLORS.primary, color: 'white', borderRadius: '12px 12px 0 0' }}>
                  <strong><i className="bi bi-building me-2"></i>{selectedSite.nom_site}</strong>
                  <button className="btn-close btn-close-white" onClick={() => { setSelectedSite(null); setSiteDetail(null); }}></button>
                </div>
                <div className="card-body" style={{ maxHeight: '580px', overflowY: 'auto' }}>
                  {loadingSiteDetail ? (
                    <div className="text-center py-4"><div className="spinner-border spinner-border-sm" style={{ color: COLORS.primary }}></div></div>
                  ) : (
                    <>
                      <p className="small text-muted mb-3">
                        <i className="bi bi-geo-alt me-1"></i>{selectedSite.addresse_site}<br />
                        <i className="bi bi-person me-1"></i>{selectedSite.nom_client}
                      </p>

                      {/* Incidents actifs du site */}
                      <h6 style={{ color: COLORS.primary }}>Incidents actifs</h6>
                      {(incidentsParSite[selectedSite.id_site] || []).length === 0 ? (
                        <div className="alert alert-success py-2 small">Aucun incident en cours — RAS.</div>
                      ) : (
                        (incidentsParSite[selectedSite.id_site] || []).map(inc => {
                          const typeLabel = (inc.type_incident?.type || '').toLowerCase();
                          const estMateriel = ['materiel', 'matériel', 'équipement', 'stock'].some(k => typeLabel.includes(k));
                          const dejaChargeMoi = inc.prise_en_charge?.id_user === userInfo?.id;
                          return (
                            <div key={inc.id_incident} className="p-2 mb-2 rounded" style={{ backgroundColor: '#FFF3E0', border: `1px solid ${COLORS.warning}40` }}>
                              <div className="d-flex justify-content-between align-items-start">
                                <span className="badge" style={{ backgroundColor: (inc.niveau?.id <= 2) ? COLORS.danger : COLORS.warning, color: 'white' }}>
                                  {NIVEAU_LABELS[inc.niveau?.id] || inc.niveau?.label}
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                  <small className="text-muted">{inc.numero_ticket_incident}</small>
                                  <Link to={`/incidents/${inc.id_incident}`} target="_blank" rel="noopener noreferrer"
                                    className="small" style={{ color: COLORS.secondary }}
                                    title="Ouvrir les détails de l'incident dans un nouvel onglet">
                                    <i className="bi bi-box-arrow-up-right"></i>
                                  </Link>
                                </div>
                              </div>
                              {inc.type_incident?.type && (
                                <p className="small mb-1 mt-1 fw-semibold" style={{ color: COLORS.darkText }}>
                                  <i className="bi bi-tag me-1"></i>
                                  {inc.type_incident.type}
                                </p>
                              )}
                              <p className="small mb-1">{inc.description_incident}</p>
                              {inc.action_affecter_incident && (
                                <p className="small mb-1" style={{ color: COLORS.darkText }}>
                                  <i className="bi bi-tools me-1"></i>
                                  <strong>Action :</strong> {inc.action_affecter_incident}
                                </p>
                              )}
                              {inc.prise_en_charge && (
                                <p className="small mb-1" style={{ color: COLORS.secondary }}>
                                  <i className="bi bi-person-check me-1"></i>
                                  Pris en charge par {inc.prise_en_charge.prenom_user} {inc.prise_en_charge.nom_user}
                                </p>
                              )}
                              <div className="d-flex flex-wrap gap-1 mt-2">
                                {!inc.prise_en_charge && (
                                  <button className="btn btn-sm" style={{ backgroundColor: COLORS.primary, color: 'white' }}
                                    disabled={actionEnCours === `charge-${inc.id_incident}`}
                                    onClick={() => prendreEnCharge(inc.id_incident)}>
                                    Prendre en charge
                                  </button>
                                )}
                                {dejaChargeMoi && (
                                  <button className="btn btn-sm" style={{ backgroundColor: COLORS.success, color: 'white' }}
                                    onClick={() => ouvrirModalResolution(inc)}>
                                    Marquer résolu
                                  </button>
                                )}
                                {estMateriel && (
                                  <button className="btn btn-sm" style={{ backgroundColor: COLORS.purple, color: 'white' }}
                                    disabled={actionEnCours === `logistique-${inc.id_incident}`}
                                    onClick={() => affecterLogistique(inc.id_incident)}>
                                    Affecter logistique
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}

                      {/* Agents en poste */}
                      <h6 className="mt-4" style={{ color: COLORS.primary }}>Agents en poste</h6>
                      {(siteDetail?.agentsAffectes || []).length === 0 ? (
                        <p className="small text-muted">Aucun agent affecté à ce site.</p>
                      ) : (
                        siteDetail.agentsAffectes.map(agent => (
                          <div key={agent.id_agent} className="d-flex justify-content-between align-items-center p-2 mb-2 rounded" style={{ backgroundColor: COLORS.lightBg }}>
                            <div>
                              <div className="fw-semibold small">{agent.nom_agent} {agent.prenom_agent}</div>
                              {agent.tel_agent && (
                                <a href={`tel:${agent.tel_agent}`} className="small" style={{ color: COLORS.secondary }}>
                                  <i className="bi bi-telephone me-1"></i>{agent.tel_agent}
                                </a>
                              )}
                              <div>
                                <span className="badge" style={{
                                  backgroundColor: agent.disponibilite === 'disponible' ? '#E8F5E9' : '#FFEBEE',
                                  color: agent.disponibilite === 'disponible' ? COLORS.success : COLORS.danger,
                                  fontSize: '0.7rem'
                                }}>
                                  {agent.disponibilite || 'disponible'}
                                </span>
                              </div>
                            </div>
                            <div className="d-flex flex-column gap-1">
                              <button className="btn btn-sm btn-outline-danger" style={{ fontSize: '0.75rem' }}
                                disabled={actionEnCours === `dispo-${agent.id_agent}`}
                                onClick={() => marquerIndisponible(agent, 'absent')}>
                                Absent/Malade
                              </button>
                            </div>
                          </div>
                        ))
                      )}

                      {/* Remplacement en cours */}
                      {remplacementAgent && (
                        <div className="p-2 mb-3 rounded" style={{ backgroundColor: '#FFF3E0', border: `1px solid ${COLORS.warning}` }}>
                          <p className="small mb-2">Remplacer <strong>{remplacementAgent.nom}</strong> par :</p>
                          {(siteDetail?.agentsDisponibles || []).length === 0 ? (
                            <p className="small text-muted mb-2">Aucun agent disponible dans la société.</p>
                          ) : (
                            siteDetail.agentsDisponibles.map(agent => (
                              <button
                                key={agent.id_agent}
                                className="btn btn-sm btn-outline-primary d-block w-100 mb-1 text-start"
                                disabled={actionEnCours === `remplace-${agent.id_agent}` || agent.disponibilite !== 'disponible'}
                                onClick={() => remplacerAgent(remplacementAgent.id_agent, agent)}
                              >
                                {agent.nom_agent} {agent.prenom_agent} {agent.disponibilite !== 'disponible' && `(${agent.disponibilite})`}
                              </button>
                            ))
                          )}
                          <button className="btn btn-sm btn-light mt-1" onClick={() => setRemplacementAgent(null)}>Annuler</button>
                        </div>
                      )}

                      {/* Envoyer une patrouille */}
                      <h6 className="mt-4" style={{ color: COLORS.primary }}>Envoyer une patrouille</h6>
                      {patrouilleSite?.id_site === selectedSite.id_site ? (
                        <div className="p-2 mb-3 rounded" style={{ backgroundColor: COLORS.lightBg }}>
                          {(siteDetail?.agentsDisponibles || []).filter(a => a.disponibilite === 'disponible').length === 0 ? (
                            <p className="small text-muted mb-2">Aucun agent disponible actuellement.</p>
                          ) : (
                            siteDetail.agentsDisponibles.filter(a => a.disponibilite === 'disponible').map(agent => (
                              <button
                                key={agent.id_agent}
                                className="btn btn-sm btn-outline-primary d-block w-100 mb-1 text-start"
                                disabled={actionEnCours === `patrouille-${agent.id_agent}`}
                                onClick={() => envoyerPatrouille(selectedSite, agent)}
                              >
                                {agent.nom_agent} {agent.prenom_agent}
                              </button>
                            ))
                          )}
                          <button className="btn btn-sm btn-light mt-1" onClick={() => setPatrouilleSite(null)}>Annuler</button>
                        </div>
                      ) : (
                        <button className="btn btn-sm mb-3" style={{ backgroundColor: COLORS.secondary, color: 'white' }}
                          onClick={() => setPatrouilleSite(selectedSite)}>
                          <i className="bi bi-signpost-2 me-1"></i>Envoyer une patrouille
                        </button>
                      )}

                      <Link to={`/sites/details/${selectedSite.id_site}`} className="btn btn-sm btn-outline-secondary w-100">
                        Voir la fiche complète du site
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Liste latérale de tous les incidents actifs, triée par gravité */}
        <div className="card mt-3" style={{ borderRadius: 12, border: `1px solid ${COLORS.lightBg}` }}>
          <div className="card-header" style={{ backgroundColor: COLORS.lightBg, color: COLORS.primary, fontWeight: 600 }}>
            <i className="bi bi-list-ul me-2"></i>Tous les incidents actifs ({totalIncidents})
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr style={{ backgroundColor: COLORS.lightBg }}>
                    <th>Gravité</th>
                    <th>Site</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Action</th>
                    <th>Pris en charge</th>
                    <th>Créé le</th>
                    <th>Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {[...incidents].sort((a, b) => (a.niveau?.id ?? 3) - (b.niveau?.id ?? 3)).map(inc => (
                    <tr key={inc.id_incident} style={{ cursor: 'pointer' }} onClick={() => {
                      const site = sites.find(s => s.id_site === inc.site?.id);
                      if (site) chargerDetailSite(site);
                    }}>
                      <td>
                        <span className="badge" style={{ backgroundColor: (inc.niveau?.id <= 2) ? COLORS.danger : COLORS.warning, color: 'white' }}>
                          {NIVEAU_LABELS[inc.niveau?.id] || inc.niveau?.label}
                        </span>
                      </td>
                      <td>{inc.site?.nom || '—'}</td>
                      <td className="small">{inc.type_incident?.type || '—'}</td>
                      <td className="small">{inc.description_incident}</td>
                      <td className="small">{inc.action_affecter_incident || <span className="text-muted">—</span>}</td>
                      <td className="small">
                        {inc.prise_en_charge ? `${inc.prise_en_charge.prenom_user} ${inc.prise_en_charge.nom_user}` : <span className="text-muted">—</span>}
                      </td>
                      <td className="small">{inc.date_creer_incident}</td>
                      <td>
                        <Link to={`/incidents/${inc.id_incident}`} target="_blank" rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="small" style={{ color: COLORS.secondary }}
                          title="Ouvrir les détails de l'incident dans un nouvel onglet">
                          <i className="bi bi-box-arrow-up-right me-1"></i>Voir
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {incidents.length === 0 && (
                    <tr><td colSpan={8} className="text-center text-muted py-4">Aucun incident actif — tout va bien.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de résolution : la description des actions effectuées est
          obligatoire, on ne peut donc pas résoudre directement depuis le bouton. */}
      {resolveModalIncident && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: '12px' }}>
                <div className="modal-header" style={{ backgroundColor: COLORS.lightBg, borderRadius: '12px 12px 0 0' }}>
                  <h5 className="modal-title mb-0" style={{ color: COLORS.primary, fontWeight: 600 }}>
                    <i className="bi bi-check-circle me-2"></i>Résoudre l'incident {resolveModalIncident.numero_ticket_incident}
                  </h5>
                  <button type="button" className="btn-close"
                    onClick={() => !resolveSubmitting && setResolveModalIncident(null)}></button>
                </div>
                <div className="modal-body">
                  <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                    Actions effectuées pour résoudre l'incident <span style={{ color: COLORS.danger }}>*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={actionResolutionText}
                    onChange={(e) => setActionResolutionText(e.target.value)}
                    placeholder="Décrivez ce qui a été fait pour résoudre ce problème (agent remplacé, intervention technique, patrouille envoyée, etc.)"
                    autoFocus
                  />
                  {resolveError && <div className="alert alert-danger py-2 mt-2 mb-0 small">{resolveError}</div>}
                </div>
                <div className="modal-footer" style={{ borderTop: `1px solid ${COLORS.lightBg}` }}>
                  <button className="btn" style={{ color: COLORS.darkText, borderColor: '#e0e0e0' }}
                    disabled={resolveSubmitting}
                    onClick={() => setResolveModalIncident(null)}>
                    Annuler
                  </button>
                  <button className="btn fw-semibold" style={{ backgroundColor: COLORS.success, color: 'white' }}
                    disabled={resolveSubmitting}
                    onClick={confirmerResolution}>
                    {resolveSubmitting ? 'Résolution...' : 'Confirmer la résolution'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};

export default PosteControle;
